// Design tokens for the FIELD console (`apps/ui`): colours, spacing, radius and type live
// here ONLY — never hard-code a hex in component code (turbo-mobile rule).
//
// Phase 17 re-aliased this file onto the Scans design system, per the token table in
// `docs/ui/design_handoff_field_console/prototype/Field Console System.dc.html#tokens`.
// Every token KEPT ITS NAME, so no screen changed for colour: `c.surface` is still
// `c.surface`, it is simply the design system's surface now. The desk console reads the
// same system through `./scans.ts`, which is the same roles under their DS names.
//
// Plain TypeScript: NO react-native import here, or a web bundle pulls RN in with it.
//
// Two things the design decided, with the numbers behind them in the handoff's README:
//   · ONE light theme. Direct sun is answered by contrast, not by a second palette — ink on
//     white holds 5.9:1 under modelled veiling glare and neutral-800 holds 4.9:1, so every
//     sentence on the phone uses one of those two. The DS dark aliases are kept below,
//     defined and unused, so switching is a data change rather than a rewrite.
//   · The primary CTA is INK, not brand. White on primary-600 falls to 3.8:1 in sun; white
//     on neutral-950 holds. Magenta keeps selection, the active stage, progress and focus —
//     shapes rather than words.

/** Bundled families. Weights are separate files — see apps/ui/src/lib/fonts.web.ts. */
export const fonts = {
  sans: 'Sora',
  /** VINs, references, timers, sizes, enum codes, uppercase eyebrows. */
  mono: 'JetBrainsMono',
} as const;

/**
 * Decision colours are paired with a glyph AND a word everywhere they appear.
 *
 * Under modelled sun the five pill words land near 3.6:1, which is why they are 14/600 and
 * never appear without their glyph. The pills are tinted, never solid — which is why
 * `onUnableToAssess` is gone: there is no solid fill left to put ink on.
 */
const light = {
  bg: '#fafbfc',
  surface: '#ffffff',
  s2: '#ffffff',
  s3: '#f6f7f8',

  border: '#e5e8eb',
  border2: '#dadde1',

  text: '#110c1d',
  /** Secondary text. The DS "muted" (neutral-600) is 3.5:1 in sun, so the field app stops at neutral-800. */
  dim: '#3b3b44',
  dim2: '#3b3b44',

  /** The CTA fill. Brand magenta lives in `brandPrimary`. */
  primary: '#110c1d',
  onPrimary: '#ffffff',
  primaryPressed: '#3b3b44',

  brandPrimary: '#bc0070',
  brandSoft: '#ffe3ee',
  brandFaint: '#fff2f7',

  decisionRepair: '#006339',
  decisionRepairBg: '#e2f1e8',
  decisionReplace: '#912800',
  decisionReplaceBg: '#fee8de',
  decisionNoDamage: '#48535c',
  decisionNoDamageBg: '#ebedef',
  decisionInsufficientEvidence: '#554876',
  decisionInsufficientEvidenceBg: '#edebf5',
  decisionUnableToAssess: '#99042e',
  decisionUnableToAssessBg: '#ffe5e5',

  noticeCaveat: '#684c09',
  noticeCaveatBg: '#f2ece1',
  stateFailed: '#99042e',
  stateFailedBg: '#ffe5e5',
  /** Held for review, and the "In review" pill: the console's own "held by you" blue. */
  stateInfo: '#1a48ac',
  stateInfoBg: '#e2edff',
  stateSuccess: '#006339',
  stateSuccessBg: '#e2f1e8',

  /** Camera surfaces and the brand panel — the one place this app is dark. */
  inverse: '#110c1d',
  inverseRaise: '#26252b',
  inverseRule: '#3b3b44',
  onInverse: '#ffffff',
  onInverseMuted: '#aab2ba',
  /**
   * Brand on the inverse surface. The light `brandPrimary` is 2.4:1 on neutral-950 and
   * unreadable there, so the design system's DARK brand value carries the eyebrow on the
   * sign-in panel. One token rather than a second palette for one line of type.
   */
  brandOnInverse: '#ffabcd',

  /** Vehicle-plan zones, the same three states as the review console's diagram. */
  zoneInClaim: '#ffcbe0',
  zoneInClaimStroke: '#fd83b7',
  zoneSelected: '#bc0070',
  zoneSelectedStroke: '#9a0058',
  zoneOff: '#f6f7f8',
  zoneOffStroke: '#e5e8eb',

  scrim: 'rgba(16,20,28,.42)',
} as const;

/** Both palettes must carry exactly the same keys; only the values differ. */
export type Palette = Record<keyof typeof light, string>;

/**
 * The design system's dark aliases. Defined, not shipped: the handoff puts dark out of
 * scope for this phase, and a theme nobody has drawn would mean inventing the chrome
 * separation and the five status pairs at implementation time.
 */
const dark: Palette = {
  bg: '#110c1d',
  surface: '#26252b',
  s2: '#26252b',
  s3: '#3b3b44',

  border: '#3b3b44',
  border2: '#4e515c',

  text: '#ebedef',
  dim: '#dadde1',
  dim2: '#aab2ba',

  primary: '#ebedef',
  onPrimary: '#110c1d',
  primaryPressed: '#dadde1',

  brandPrimary: '#ffabcd',
  brandSoft: '#50002a',
  brandFaint: '#2a0018',

  decisionRepair: '#a7d6ba',
  decisionRepairBg: '#004a28',
  decisionReplace: '#f5b79b',
  decisionReplaceBg: '#5a1600',
  decisionNoDamage: '#c4cad0',
  decisionNoDamageBg: '#3b3b44',
  decisionInsufficientEvidence: '#c7bfe4',
  decisionInsufficientEvidenceBg: '#2f2745',
  decisionUnableToAssess: '#ffb0b3',
  decisionUnableToAssessBg: '#760020',

  noticeCaveat: '#d9c7a7',
  noticeCaveatBg: '#4e3801',
  stateFailed: '#ffb0b3',
  stateFailedBg: '#760020',
  stateInfo: '#b7cdff',
  stateInfoBg: '#123167',
  stateSuccess: '#a7d6ba',
  stateSuccessBg: '#004a28',

  inverse: '#0b0812',
  inverseRaise: '#26252b',
  inverseRule: '#4e515c',
  onInverse: '#ffffff',
  onInverseMuted: '#aab2ba',
  brandOnInverse: '#ffabcd',

  zoneInClaim: '#50002a',
  zoneInClaimStroke: '#9a0058',
  zoneSelected: '#ffabcd',
  zoneSelectedStroke: '#ffcbe0',
  zoneOff: '#3b3b44',
  zoneOffStroke: '#4e515c',

  scrim: 'rgba(16,20,28,.42)',
};

export const palettes: Record<'light' | 'dark', Palette> = { light, dark };
export type ThemeName = keyof typeof palettes;

/**
 * The type ramp, cut to the design system's: 10 / 11 / 12 / 14 / 16 / 20 / 24 and nothing
 * else. The 30 and 26 that used to open a screen are gone — on a 390 pt phone the DS's 24
 * is the page title, and dropping two steps is what keeps the two products in one family.
 *
 * Single-line values take their height from `lineHeight` on the 16 / 24 / 32 / 40 / 48
 * rhythm; wrapping values use the 1.45 ratio and are given no lineHeight here.
 */
export const type = {
  /** The verdict word. */
  display: {
    fontFamily: fonts.sans,
    fontSize: 24,
    lineHeight: 32,
    fontWeight: '600' as const,
    letterSpacing: -0.5,
  },
  h1: {
    fontFamily: fonts.sans,
    fontSize: 24,
    lineHeight: 32,
    fontWeight: '600' as const,
    letterSpacing: -0.5,
  },
  h2: {
    fontFamily: fonts.sans,
    fontSize: 20,
    lineHeight: 32,
    fontWeight: '600' as const,
    letterSpacing: -0.4,
  },
  /** Long-form paragraphs: the justification, the stage notes. */
  bodyLg: { fontFamily: fonts.sans, fontSize: 16, lineHeight: 24, fontWeight: '400' as const },
  body: { fontFamily: fonts.sans, fontSize: 14, lineHeight: 20, fontWeight: '400' as const },
  /** Row titles and button labels. */
  label: { fontFamily: fonts.sans, fontSize: 14, lineHeight: 24, fontWeight: '600' as const },
  sub: { fontFamily: fonts.sans, fontSize: 12, lineHeight: 16, fontWeight: '400' as const },
  caption: { fontFamily: fonts.sans, fontSize: 12, lineHeight: 16, fontWeight: '400' as const },
  micro: { fontFamily: fonts.sans, fontSize: 11, lineHeight: 16, fontWeight: '400' as const },
  /** Section labels and step counters. Uppercase is applied by the component. */
  monoEyebrow: {
    fontFamily: fonts.sans,
    fontSize: 11,
    lineHeight: 16,
    fontWeight: '600' as const,
    letterSpacing: 0.9,
  },
  /** VIN, elapsed, MB, tags. Mono earns its place by aligning figures. */
  monoValue: {
    fontFamily: fonts.mono,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500' as const,
    letterSpacing: 0.5,
  },
  /** The VIN in the confirm sheet. */
  monoTitle: {
    fontFamily: fonts.mono,
    fontSize: 20,
    lineHeight: 32,
    fontWeight: '600' as const,
    letterSpacing: 0.8,
  },
} as const;

/**
 * Theme-INDEPENDENT tokens: identical in both palettes, so they are read directly rather
 * than through the theme hook.
 *
 * Spacing is the design system's 2 / 4 / 8 / 16 / 24 / 32 / 48 / 64. Radius keeps its old
 * names and takes the DS's four values — 4 for chips, 8 for controls, 12 for cards and
 * sheets, 999 for pills — so the 6 / 10 / 14 / 20 radii are gone without touching a screen.
 */
export const tokens = {
  spacing: { xxs: 2, xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48, xxxl: 64 },
  radius: { sm: 4, md: 8, lg: 12, xl: 12, sheet: 12, pill: 999 },
  /** 44 pt minimum, no exceptions; the primary CTA is a 48 line-height; the shutter is 78. */
  touch: { min: 44, cta: 48, shutter: 78 },
} as const;

/**
 * Motion, from the handoff §7.6. Durations in ms.
 *
 * Only three things move. What must NOT: the viewfinder and its overlays, the bank-card
 * outline, and the payload meter — a meter that animates reads as upload progress.
 */
export const motion = {
  /** The auto-advance bar fills linearly; "Stay" cancels it. */
  autoAdvance: { ms: 3200, easing: 'linear' },
  /** A finished stage dot fills and its connector draws. */
  stage: { ms: 240, easing: 'ease-out' },
  /** The active stage dot breathes once every two seconds. The held ring never pulses. */
  pulse: { ms: 2000, easing: 'ease-in-out' },
  /** The undo toast rises 8 px on the way in and leaves faster than it arrived. */
  toastIn: { ms: 160, easing: 'ease-out', translateY: 8 },
  toastOut: { ms: 120, easing: 'ease-in' },
  toastHold: { ms: 5000 },
} as const;
